/**
 * Imported Offer Detail Page - Vollständig bearbeitbares Angebot
 * 
 * Features:
 * - Angebotsübersicht mit Standard-Nummer
 * - Positionen inline bearbeiten, hinzufügen, löschen
 * - Kopfdaten bearbeiten (Titel, Datum, MwSt)
 * - Abschlagsrechnungen & Schlussrechnung
 * - Rechnungshistorie
 * - Normalisierung alter Nummern
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, getErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import IncognitoToggleButton from "../components/IncognitoToggleButton";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Receipt, Plus, Loader2, CheckCircle,
  AlertTriangle, Lock, Euro, Percent, Calculator, Eye,
  MoreHorizontal, Pencil, Trash2, Save, X, RefreshCw, Printer, Download
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try { return new Date(dateStr).toLocaleDateString("de-DE"); }
  catch { return dateStr; }
};

const InvoiceTypeBadge = ({ type }) => {
  const config = {
    abschlag: { label: "Abschlag", variant: "outline", className: "border-blue-500 text-blue-500" },
    schluss: { label: "Schluss", variant: "outline", className: "border-green-500 text-green-500" },
    standard: { label: "Standard", variant: "secondary" }
  };
  const c = config[type] || config.standard;
  return <Badge variant={c.variant} className={c.className}>{c.label}</Badge>;
};

const StatusBadge = ({ status }) => {
  const config = {
    draft: { label: "Entwurf", className: "bg-gray-100 text-gray-700" },
    entwurf: { label: "Entwurf", className: "bg-gray-100 text-gray-700" },
    sent: { label: "Versendet", className: "bg-blue-100 text-blue-700" },
    final: { label: "Finalisiert", className: "bg-blue-100 text-blue-700" },
    paid: { label: "Bezahlt", className: "bg-green-100 text-green-700" },
    cancelled: { label: "Storniert", className: "bg-red-100 text-red-700" }
  };
  const c = config[status] || { label: status || "Unbekannt", className: "bg-gray-100" };
  return <Badge className={c.className}>{c.label}</Badge>;
};

export default function ImportedOfferDetailPage() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [invoices, setInvoices] = useState([]);

  // Edit state
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Dialogs
  const [showPartialDialog, setShowPartialDialog] = useState(false);
  const [showFinalDialog, setShowFinalDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [finalizingInvoice, setFinalizingInvoice] = useState(null);
  const [normalizing, setNormalizing] = useState(false);

  // Partial Invoice Form
  const [partialPercentage, setPartialPercentage] = useState(60);
  const [partialDescription, setPartialDescription] = useState("");
  const [reopenFinalAfterAdvance, setReopenFinalAfterAdvance] = useState(false);

  // New Item Form
  const [newItem, setNewItem] = useState({
    title: "", description: "", quantity: 1, unit: "Stk", unit_price_net: 0
  });

  const authHeaders = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };

  const loadOffer = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/offer-import/offer/${id}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Angebot nicht gefunden");
      const data = await res.json();
      setOffer(data.offer);
      setCalculation(data.calculation);
      setInvoices(data.invoices || []);
    } catch (error) {
      toast.error(typeof error.message === 'string' ? error.message : "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => { loadOffer(); }, [loadOffer]);

  // Auto-open partial or final invoice dialog when arriving with ?openInvoice=partial|final
  useEffect(() => {
    if (!offer) return;
    const openInvoice = searchParams.get("openInvoice");
    if (openInvoice) {
      if (openInvoice === "final" || openInvoice === "schluss" || openInvoice === "schlussrechnung") {
        setShowFinalDialog(true);
      } else {
        // partial / abschlag / "1" (legacy) → partial dialog
        setShowPartialDialog(true);
      }
      searchParams.delete("openInvoice");
      setSearchParams(searchParams, { replace: true });
    }
  }, [offer, searchParams, setSearchParams]);

  // ===== NORMALIZE OFFER NUMBER =====
  const handleNormalize = async () => {
    setNormalizing(true);
    try {
      const res = await fetch(`${API_URL}/api/offer-import/offer/${id}/normalize`, {
        method: "POST", headers: authHeaders
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Fehler bei Normalisierung");
      }
      const data = await res.json();
      toast.success(`Neue Nummer: ${data.offer_number}`);
      await loadOffer();
    } catch (error) {
      toast.error(typeof error.message === 'string' ? error.message : "Fehler");
    } finally {
      setNormalizing(false);
    }
  };

  // ===== HEADER EDITING =====
  const startEditHeader = () => {
    setHeaderForm({
      title: offer.title || "",
      offer_date: offer.offer_date || "",
      vat_rate: offer.vat_rate ?? 19,
      status: offer.status || "accepted"
    });
    setEditingHeader(true);
  };

  const saveHeader = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/offer-import/offer/${id}`, {
        method: "PUT", headers: authHeaders,
        body: JSON.stringify(headerForm)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Fehler beim Speichern");
      }
      toast.success("Angebot aktualisiert");
      setEditingHeader(false);
      await loadOffer();
    } catch (error) {
      toast.error(typeof error.message === 'string' ? error.message : "Fehler");
    } finally {
      setSaving(false);
    }
  };

  // ===== LINE ITEM EDITING =====
  const startEditItem = (item) => {
    setEditingItem(item.id);
    setEditForm({
      title: item.title || "",
      description: item.description || "",
      quantity: item.quantity || 1,
      unit: item.unit || "Stk",
      unit_price_net: item.unit_price_net || 0
    });
  };

  const saveItem = async (itemId) => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        quantity: parseFloat(editForm.quantity) || 1,
        unit_price_net: parseFloat(editForm.unit_price_net) || 0,
        total_price_net: (parseFloat(editForm.quantity) || 1) * (parseFloat(editForm.unit_price_net) || 0)
      };
      await api.put(`/offer-import/offer/${id}/line-item/${itemId}`, payload);
      toast.success("Position aktualisiert");
      setEditingItem(null);
      await loadOffer();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/offer-import/offer/${id}/line-item/${itemId}`);
      toast.success("Position geloescht");
      await loadOffer();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Fehler beim Loeschen");
    }
  };

  const addItem = async () => {
    setSaving(true);
    try {
      const payload = {
        ...newItem,
        quantity: parseFloat(newItem.quantity) || 1,
        unit_price_net: parseFloat(newItem.unit_price_net) || 0
      };
      await api.post(`/offer-import/offer/${id}/line-item`, payload);
      toast.success("Position hinzugefuegt");
      setShowAddDialog(false);
      setNewItem({ title: "", description: "", quantity: 1, unit: "Stk", unit_price_net: 0 });
      await loadOffer();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Fehler beim Hinzufuegen");
    } finally {
      setSaving(false);
    }
  };

  // ===== INVOICING =====
  const handleCreatePartialInvoice = async () => {
    if (partialPercentage <= 0 || partialPercentage > 100) {
      toast.error("Prozentsatz muss zwischen 1 und 100 liegen");
      return;
    }
    setCreatingInvoice(true);
    try {
      await api.post("/offer-import/invoice/partial", {
        offer_id: id,
        percentage: partialPercentage,
        description: partialDescription || `Abschlagsrechnung ${partialPercentage}%`
      });
      toast.success("Abschlagsrechnung erstellt");
      setShowPartialDialog(false);
      setPartialPercentage(60);
      setPartialDescription("");
      await loadOffer();
    } catch (error) {
      console.error("[partial-invoice] error:", error?.response?.status, error?.response?.data);
      const msg = getErrorMessage(error, "Fehler bei Rechnungserstellung");
      toast.error("Fehler bei Rechnungserstellung", { description: msg, duration: 10000 });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleCreateFinalInvoice = async () => {
    setCreatingInvoice(true);
    try {
      // axios uses XHR → bypasses the Emergent platform fetch-wrapper that consumes 4xx bodies
      const response = await api.post(`/offer-import/offer/${id}/final-invoice`, { payment_days: 14 });
      const created = response.data;
      toast.success(`Schlussrechnung ${created.invoice_number} erstellt`);
      setShowFinalDialog(false);
      await loadOffer();
    } catch (error) {
      console.error("[final-invoice] error:", error?.response?.status, error?.response?.data);
      const msg = getErrorMessage(error, "Fehler bei Rechnungserstellung");
      toast.error("Fehler bei Rechnungserstellung", { description: msg, duration: 10000 });
    } finally {
      setCreatingInvoice(false);
    }
  };

  // ============ EXTERNE ABSCHLAGSZAHLUNGEN ============
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ invoice_number: "", amount: "", payment_date: "", notes: "" });
  const [editingAdvanceId, setEditingAdvanceId] = useState(null);
  const [savingAdvance, setSavingAdvance] = useState(false);

  const openAddAdvance = () => {
    setEditingAdvanceId(null);
    setAdvanceForm({ invoice_number: "", amount: "", payment_date: "", notes: "" });
    setShowAdvanceDialog(true);
  };

  const openEditAdvance = (payment) => {
    setEditingAdvanceId(payment.id);
    setAdvanceForm({
      invoice_number: payment.invoice_number || "",
      amount: String(payment.amount ?? ""),
      payment_date: payment.payment_date || "",
      notes: payment.notes || "",
    });
    setShowAdvanceDialog(true);
  };

  const handleSaveAdvance = async () => {
    if (!advanceForm.invoice_number.trim()) {
      toast.error("Rechnungsnummer ist Pflichtfeld");
      return;
    }
    const amt = parseFloat(advanceForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Betrag muss eine positive Zahl sein");
      return;
    }
    setSavingAdvance(true);
    try {
      const path = editingAdvanceId
        ? `/offer-import/offer/${id}/advance-payments/${editingAdvanceId}`
        : `/offer-import/offer/${id}/advance-payments`;
      const payload = {
        invoice_number: advanceForm.invoice_number.trim(),
        amount: amt,
        payment_date: advanceForm.payment_date || null,
        notes: advanceForm.notes || null,
      };
      if (editingAdvanceId) {
        await api.put(path, payload);
      } else {
        await api.post(path, payload);
      }
      toast.success(editingAdvanceId ? "Abschlagszahlung aktualisiert" : "Abschlagszahlung hinzugefügt");
      setShowAdvanceDialog(false);
      await loadOffer();
      if (reopenFinalAfterAdvance) {
        setReopenFinalAfterAdvance(false);
        // small delay so the updated calculation is reflected
        setTimeout(() => setShowFinalDialog(true), 250);
      }
    } catch (error) {
      console.error("[save-advance] error:", error?.response?.status, error?.response?.data);
      const msg = getErrorMessage(error, "Fehler beim Speichern der Abschlagszahlung");
      toast.error("Fehler beim Speichern", { description: msg, duration: 10000 });
    } finally {
      setSavingAdvance(false);
    }
  };

  const handleDeleteAdvance = async (paymentId) => {
    if (!window.confirm("Abschlagszahlung wirklich löschen?")) return;
    try {
      await api.delete(`/offer-import/offer/${id}/advance-payments/${paymentId}`);
      toast.success("Abschlagszahlung gelöscht");
      await loadOffer();
    } catch (error) {
      console.error("[delete-advance] error:", error?.response?.status, error?.response?.data);
      const msg = getErrorMessage(error, "Fehler beim Löschen");
      toast.error("Fehler beim Löschen", { description: msg, duration: 10000 });
    }
  };

  const handleFinalizeInvoice = async (invoiceId) => {
    setFinalizingInvoice(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/finalize`, {
        method: "POST", headers: authHeaders
      });
      if (!res.ok) {
        let msg = "Fehler beim Finalisieren";
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        throw new Error(msg);
      }
      toast.success("Rechnung finalisiert");
      await loadOffer();
    } catch (error) {
      toast.error(typeof error.message === 'string' ? error.message : "Fehler");
    } finally {
      setFinalizingInvoice(null);
    }
  };

  const handleViewPdf = (invoiceId) => {
    window.open(`${API_URL}/api/invoices/${invoiceId}/pdf?token=${token}`, "_blank");
  };

  const handlePreviewPdf = (invoiceId) => {
    window.open(`${API_URL}/api/invoices/${invoiceId}/preview-pdf?token=${token}`, "_blank");
  };

  // === OFFER PDF ===
  const handleOfferPdf = () => {
    window.open(`${API_URL}/api/offer-import/offer/${id}/pdf?token=${token}`, "_blank");
  };

  // === PDF AKTUALISIEREN ===
  const [refreshingPdf, setRefreshingPdf] = useState(false);
  const handleRefreshPdf = async () => {
    setRefreshingPdf(true);
    try {
      // Zuerst pdf_generated zurücksetzen und Berechnung aktualisieren
      const res = await fetch(`${API_URL}/api/offers/${id}/refresh-pdf`, {
        method: "POST", headers: authHeaders
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Fehler beim Aktualisieren");
      }
      toast.success("PDF wird aktualisiert...");
      await loadOffer();
      // PDF direkt öffnen
      handleOfferPdf();
    } catch (error) {
      toast.error(typeof error.message === 'string' ? error.message : "Fehler");
    } finally {
      setRefreshingPdf(false);
    }
  };

  // === STATUS UPDATE ===
  const handleStatusChange = async (newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/offer-import/offer/${id}`, {
        method: "PUT", headers: authHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Fehler beim Statuswechsel");
      }
      toast.success(`Status auf "${statusLabels[newStatus] || newStatus}" gesetzt`);
      await loadOffer();
    } catch (error) {
      toast.error(typeof error.message === 'string' ? error.message : "Fehler");
    }
  };

  const statusLabels = {
    "erstellt": "Erstellt",
    "gesendet": "Gesendet",
    "accepted": "Angenommen",
    "angenommen": "Angenommen",
    "abgelehnt": "Abgelehnt",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold">Angebot nicht gefunden</h2>
        <Button variant="link" onClick={() => navigate(-1)}>Zurueck</Button>
      </div>
    );
  }

  const isLocked = offer.price_locked;
  const hasStandardNumber = /^A-\d{4}-\d{4}$/.test(offer.offer_number || "");
  const progressPercent = calculation ? (calculation.amount_invoiced / calculation.total_gross * 100) : 0;
  const currentStatus = offer.status || "erstellt";

  return (
    <div className="space-y-6" data-testid="imported-offer-detail">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold" data-testid="offer-number">
                {offer.offer_number || "Ohne Nummer"}
              </h1>
              {offer.legacy_offer_number && offer.legacy_offer_number !== offer.offer_number && (
                <Badge variant="outline" className="text-xs text-muted-foreground" data-testid="legacy-number-badge">
                  ehem. {offer.legacy_offer_number}
                </Badge>
              )}
              {/* Status-Dropdown */}
              <Select value={currentStatus} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="status-select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="erstellt">Erstellt</SelectItem>
                  <SelectItem value="gesendet">Gesendet</SelectItem>
                  <SelectItem value="angenommen">Angenommen</SelectItem>
                  <SelectItem value="abgelehnt">Abgelehnt</SelectItem>
                </SelectContent>
              </Select>
              {isLocked && (
                <Badge variant="outline" className="border-amber-500 text-amber-500">
                  <Lock className="h-3 w-3 mr-1" />Gesperrt
                </Badge>
              )}
              <IncognitoBadge active={offer.is_incognito} size="md" />
              <IncognitoToggleButton user={user} docType="legacy_offer" docId={offer.id} active={offer.is_incognito} onToggled={loadOffer} />
              {!hasStandardNumber && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNormalize}
                  disabled={normalizing}
                  data-testid="normalize-button"
                >
                  {normalizing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  Standard-Nr. zuweisen
                </Button>
              )}
            </div>
            {!editingHeader ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <span>Kunde: {offer.customer_name}</span>
                {offer.title && <span>| {offer.title}</span>}
                {offer.offer_date && <span>| {formatDate(offer.offer_date)}</span>}
                {!isLocked && (
                  <Button variant="ghost" size="sm" className="h-6 px-2" onClick={startEditHeader} data-testid="edit-header-button">
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Input
                  value={headerForm.title}
                  onChange={(e) => setHeaderForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Titel"
                  className="w-48 h-8 text-sm"
                  data-testid="edit-header-title"
                />
                <Input
                  type="date"
                  value={headerForm.offer_date}
                  onChange={(e) => setHeaderForm(prev => ({ ...prev, offer_date: e.target.value }))}
                  className="w-40 h-8 text-sm"
                  data-testid="edit-header-date"
                />
                <Input
                  type="number"
                  value={headerForm.vat_rate}
                  onChange={(e) => {
                    const v = e.target.value;
                    // 0 % MwSt muss erlaubt sein (§12 Abs. 3 UStG für private PV-Anlagen).
                    // Leeres Feld → Default 19; sonst geparster Wert (inkl. 0).
                    const parsed = parseFloat(v);
                    setHeaderForm(prev => ({ ...prev, vat_rate: v === "" || Number.isNaN(parsed) ? 19 : parsed }));
                  }}
                  className="w-20 h-8 text-sm"
                  placeholder="MwSt %"
                  data-testid="edit-header-vat"
                />
                <Button size="sm" className="h-8" onClick={saveHeader} disabled={saving} data-testid="save-header-button">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingHeader(false)} data-testid="cancel-header-button">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {/* PDF-Buttons */}
          <Button variant="outline" onClick={handleOfferPdf} data-testid="offer-pdf-button">
            <Printer className="h-4 w-4 mr-2" />PDF anzeigen
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefreshPdf} 
            disabled={refreshingPdf}
            data-testid="refresh-pdf-button"
            title="Berechnung aktualisieren und PDF im neuen Layout erstellen"
          >
            {refreshingPdf ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            PDF aktualisieren
          </Button>

          <Dialog open={showPartialDialog} onOpenChange={setShowPartialDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!calculation || calculation.amount_remaining <= 0} data-testid="create-partial-invoice-button">
                <Percent className="h-4 w-4 mr-2" />Abschlagsrechnung
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Abschlagsrechnung erstellen</DialogTitle>
                <DialogDescription>Erstellen Sie eine prozentuale Abschlagsrechnung.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Prozentsatz</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" max="100" value={partialPercentage}
                      onChange={(e) => setPartialPercentage(parseFloat(e.target.value) || 0)} className="w-24"
                      data-testid="partial-percentage-input" />
                    <span>%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    = {formatCurrency((calculation?.total_gross || 0) * partialPercentage / 100)}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Beschreibung (optional)</Label>
                  <Input value={partialDescription}
                    onChange={(e) => setPartialDescription(e.target.value)}
                    placeholder={`${invoices.filter(i => i.invoice_type === "abschlag").length + 1}. Abschlagsrechnung`}
                    data-testid="partial-description-input" />
                </div>
                <div className="flex gap-2">
                  <span className="text-sm text-muted-foreground">Schnellauswahl:</span>
                  {[30, 40, 50, 60].map(p => (
                    <Button key={p} variant="outline" size="sm" onClick={() => setPartialPercentage(p)}>{p}%</Button>
                  ))}
                </div>
                <div className="bg-muted/50 rounded p-3 text-sm">
                  <div className="flex justify-between">
                    <span>Angebotssumme:</span><span>{formatCurrency(calculation?.total_gross)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bereits abgerechnet:</span><span>{formatCurrency(calculation?.amount_invoiced)}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-medium">
                    <span>Noch offen:</span><span>{formatCurrency(calculation?.amount_remaining)}</span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPartialDialog(false)}>Abbrechen</Button>
                <Button onClick={handleCreatePartialInvoice} disabled={creatingInvoice} data-testid="submit-partial-invoice">
                  {creatingInvoice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Erstellen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <AlertDialog open={showFinalDialog} onOpenChange={setShowFinalDialog}>
            <AlertDialogTrigger asChild>
              <Button disabled={!calculation || calculation.amount_remaining <= 0} data-testid="create-final-invoice-button">
                <Calculator className="h-4 w-4 mr-2" />Schlussrechnung
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Schlussrechnung erstellen</AlertDialogTitle>
                <AlertDialogDescription>
                  Die Schlussrechnung enthält den Gesamtbetrag abzüglich aller bisherigen Abschläge.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="bg-muted/50 rounded p-4 text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Angebotssumme:</span><span>{formatCurrency(calculation?.total_gross)}</span>
                </div>
                {invoices.filter(i => i.invoice_type === "abschlag" && i.status !== "cancelled").map((inv, idx) => (
                  <div key={inv.id} className="flex justify-between text-muted-foreground">
                    <span>- {inv.title || `${idx + 1}. Abschlag (intern)`}:</span>
                    <span>- {formatCurrency(inv.gross_total)}</span>
                  </div>
                ))}
                {(offer?.external_advance_payments || []).map((p) => (
                  <div key={p.id} className="flex justify-between text-muted-foreground">
                    <span>- Extern: {p.invoice_number}:</span>
                    <span>- {formatCurrency(p.amount)}</span>
                  </div>
                ))}
                <Separator className="my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Restbetrag (Schlussrechnung):</span>
                  <span data-testid="final-invoice-remaining">{formatCurrency(calculation?.amount_remaining)}</span>
                </div>
              </div>
              {/* Quick-add for external advance payments directly from this dialog */}
              <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-amber-300 bg-amber-50/60 p-3 text-sm">
                <div className="flex-1">
                  <div className="font-medium text-amber-900">Bereits außerhalb des CRM gezahlte Abschläge?</div>
                  <div className="text-amber-800/80 text-xs mt-0.5">
                    Erfasse sie mit Rechnungsnummer + Betrag, damit sie vom Restbetrag abgezogen werden.
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-amber-400 text-amber-900 hover:bg-amber-100"
                  data-testid="quick-add-external-advance-from-final"
                  onClick={() => {
                    setShowFinalDialog(false);
                    setReopenFinalAfterAdvance(true);
                    openAddAdvance();
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Erfassen
                </Button>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={handleCreateFinalInvoice} disabled={creatingInvoice} data-testid="submit-final-invoice">
                  {creatingInvoice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Schlussrechnung erstellen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Progress */}
      {calculation && calculation.total_gross > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Abrechnungsfortschritt</span>
              <span className="text-sm text-muted-foreground">
                {formatCurrency(calculation.amount_invoiced)} von {formatCurrency(calculation.total_gross)}
              </span>
            </div>
            <Progress value={Math.min(progressPercent, 100)} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>0%</span>
              <span>{progressPercent.toFixed(0)}% abgerechnet</span>
              <span>100%</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="positions">
        <TabsList>
          <TabsTrigger value="positions" data-testid="tab-positions">Positionen</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Rechnungen ({invoices.length})</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Zusammenfassung</TabsTrigger>
        </TabsList>

        {/* === POSITIONS TAB === */}
        <TabsContent value="positions" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Angebotspositionen</CardTitle>
                  {isLocked && (
                    <CardDescription className="flex items-center gap-1 text-amber-600 mt-1">
                      <Lock className="h-4 w-4" />
                      Positionen sind nach Rechnungsstellung gesperrt
                    </CardDescription>
                  )}
                </div>
                {!isLocked && (
                  <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="add-position-button">
                        <Plus className="h-4 w-4 mr-1" />Position
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Neue Position</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 py-2">
                        <div>
                          <Label>Titel</Label>
                          <Input value={newItem.title}
                            onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Positionsbezeichnung"
                            data-testid="new-item-title" />
                        </div>
                        <div>
                          <Label>Beschreibung</Label>
                          <Input value={newItem.description}
                            onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Optional"
                            data-testid="new-item-description" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label>Menge</Label>
                            <Input type="number" value={newItem.quantity}
                              onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
                              data-testid="new-item-quantity" />
                          </div>
                          <div>
                            <Label>Einheit</Label>
                            <Input value={newItem.unit}
                              onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                              data-testid="new-item-unit" />
                          </div>
                          <div>
                            <Label>EP netto</Label>
                            <Input type="number" step="0.01" value={newItem.unit_price_net}
                              onChange={(e) => setNewItem(prev => ({ ...prev, unit_price_net: e.target.value }))}
                              data-testid="new-item-price" />
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Gesamt: {formatCurrency((parseFloat(newItem.quantity) || 0) * (parseFloat(newItem.unit_price_net) || 0))}
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddDialog(false)}>Abbrechen</Button>
                        <Button onClick={addItem} disabled={saving || !newItem.title} data-testid="submit-new-item">
                          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                          Hinzufuegen
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Pos.</TableHead>
                    <TableHead>Beschreibung</TableHead>
                    <TableHead className="text-right w-20">Menge</TableHead>
                    <TableHead className="w-16">Einheit</TableHead>
                    <TableHead className="text-right w-28">EP (netto)</TableHead>
                    <TableHead className="text-right w-28">Gesamt</TableHead>
                    {!isLocked && <TableHead className="w-20 text-right">Aktionen</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(offer.line_items || []).map((item) => (
                    editingItem === item.id ? (
                      <TableRow key={item.id} className="bg-muted/30">
                        <TableCell className="font-mono">{item.line_no}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Input value={editForm.title}
                              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                              className="h-8 text-sm" data-testid={`edit-item-title-${item.id}`} />
                            <Input value={editForm.description}
                              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                              className="h-7 text-xs" placeholder="Beschreibung"
                              data-testid={`edit-item-desc-${item.id}`} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={editForm.quantity}
                            onChange={(e) => setEditForm(prev => ({ ...prev, quantity: e.target.value }))}
                            className="h-8 text-sm text-right w-20" data-testid={`edit-item-qty-${item.id}`} />
                        </TableCell>
                        <TableCell>
                          <Input value={editForm.unit}
                            onChange={(e) => setEditForm(prev => ({ ...prev, unit: e.target.value }))}
                            className="h-8 text-sm w-16" data-testid={`edit-item-unit-${item.id}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" value={editForm.unit_price_net}
                            onChange={(e) => setEditForm(prev => ({ ...prev, unit_price_net: e.target.value }))}
                            className="h-8 text-sm text-right w-28" data-testid={`edit-item-price-${item.id}`} />
                        </TableCell>
                        <TableCell className="text-right font-medium text-sm">
                          {formatCurrency((parseFloat(editForm.quantity) || 0) * (parseFloat(editForm.unit_price_net) || 0))}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                              onClick={() => saveItem(item.id)} disabled={saving}
                              data-testid={`save-item-${item.id}`}>
                              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 text-green-600" />}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                              onClick={() => setEditingItem(null)} data-testid={`cancel-edit-${item.id}`}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={item.id} className={item.is_optional ? "opacity-60" : ""}>
                        <TableCell className="font-mono text-sm">{item.line_no}</TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{item.title}</div>
                          {item.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm">{item.quantity}</TableCell>
                        <TableCell className="text-sm">{item.unit}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(item.unit_price_net)}</TableCell>
                        <TableCell className="text-right font-medium text-sm">{formatCurrency(item.total_price_net)}</TableCell>
                        {!isLocked && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                onClick={() => startEditItem(item)} data-testid={`edit-item-${item.id}`}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                    data-testid={`delete-item-${item.id}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Position loeschen?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      "{item.title}" wird unwiderruflich geloescht.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteItem(item.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                      data-testid={`confirm-delete-${item.id}`}>
                                      Loeschen
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
              {(!offer.line_items || offer.line_items.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  Keine Positionen vorhanden
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === INVOICES TAB === */}
        <TabsContent value="invoices" className="mt-4 space-y-6">
          {/* === EXTERNE ABSCHLAGSZAHLUNGEN === */}
          <Card data-testid="external-advances-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Externe Abschlagszahlungen</CardTitle>
                <CardDescription>
                  Bereits geleistete Anzahlungen, die NICHT über das CRM ausgestellt wurden
                  (z. B. aus dem Altsystem). Werden vom Auftragsendbetrag abgezogen und
                  bei der Schlussrechnung aufgeführt.
                </CardDescription>
              </div>
              <Button onClick={openAddAdvance} data-testid="add-external-advance-button" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Hinzufügen
              </Button>
            </CardHeader>
            <CardContent>
              {!(offer?.external_advance_payments?.length) ? (
                <div className="text-center py-6 text-sm text-muted-foreground border border-dashed rounded-md">
                  Keine externen Abschlagszahlungen erfasst.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rechnungsnummer</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Betrag</TableHead>
                      <TableHead>Bemerkungen</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {offer.external_advance_payments.map((p) => (
                      <TableRow key={p.id} data-testid={`ext-advance-row-${p.id}`}>
                        <TableCell className="font-mono text-sm">{p.invoice_number}</TableCell>
                        <TableCell className="text-sm">{formatDate(p.payment_date)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(p.amount)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{p.notes || "-"}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditAdvance(p)} title="Bearbeiten">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAdvance(p.id)} title="Löschen" data-testid={`delete-ext-advance-${p.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold bg-amber-50">
                      <TableCell colSpan={2}>Summe externe Abschlagszahlungen</TableCell>
                      <TableCell className="text-right" data-testid="external-advances-total">
                        {formatCurrency(calculation?.external_advances_total ?? 0)}
                      </TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Rechnungen</CardTitle></CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Noch keine Rechnungen erstellt</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nummer</TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead>Titel</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Betrag</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => {
                      const isDraft = inv.status === "draft" || inv.status === "entwurf";
                      return (
                        <TableRow key={inv.id}>
                          <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                          <TableCell><InvoiceTypeBadge type={inv.invoice_type} /></TableCell>
                          <TableCell className="text-sm">{inv.title}</TableCell>
                          <TableCell className="text-sm">{formatDate(inv.invoice_date)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(inv.gross_total)}</TableCell>
                          <TableCell><StatusBadge status={inv.status} /></TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {isDraft ? (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => handlePreviewPdf(inv.id)} title="Vorschau">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button variant="default" size="sm"
                                    onClick={() => handleFinalizeInvoice(inv.id)}
                                    disabled={finalizingInvoice === inv.id}
                                    className="bg-green-600 hover:bg-green-700"
                                    data-testid={`finalize-invoice-${inv.id}`}>
                                    {finalizingInvoice === inv.id
                                      ? <Loader2 className="h-4 w-4 animate-spin" />
                                      : <><CheckCircle className="h-4 w-4 mr-1" />Finalisieren</>}
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => handleViewPdf(inv.id)} title="PDF">
                                    <FileText className="h-4 w-4" />
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem asChild>
                                        <Link to="/rechnungen" className="cursor-pointer">Zur Rechnungsuebersicht</Link>
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === SUMMARY TAB === */}
        <TabsContent value="summary" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Angebotssummen</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Summe Positionen (netto):</span>
                  <span>{formatCurrency(calculation?.positions_total)}</span>
                </div>
                {calculation?.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Rabatt:</span>
                    <span>- {formatCurrency(calculation.discount_amount)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-sm">
                  <span>Netto nach Rabatt:</span>
                  <span>{formatCurrency(calculation?.subtotal_after_discount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>MwSt ({offer.vat_rate ?? 19}%):</span>
                  <span>{formatCurrency(calculation?.vat_amount)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>Gesamt (brutto):</span>
                  <span>{formatCurrency(calculation?.total_gross)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Abrechnungsstatus</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Angebotssumme:</span>
                  <span>{formatCurrency(calculation?.total_gross)}</span>
                </div>
                <div className="flex justify-between text-sm text-green-600">
                  <span>Bereits abgerechnet:</span>
                  <span>{formatCurrency(calculation?.amount_invoiced)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>Noch offen:</span>
                  <span>{formatCurrency(calculation?.amount_remaining)}</span>
                </div>
                {calculation?.has_discrepancy && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
                    <AlertTriangle className="h-4 w-4 inline mr-1" />
                    Summenabweichung: {formatCurrency(calculation.discrepancy_amount)}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* === Dialog: Externe Abschlagszahlung hinzufügen/bearbeiten === */}
      <Dialog open={showAdvanceDialog} onOpenChange={setShowAdvanceDialog}>
        <DialogContent data-testid="ext-advance-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingAdvanceId ? "Abschlagszahlung bearbeiten" : "Externe Abschlagszahlung erfassen"}
            </DialogTitle>
            <DialogDescription>
              Erfassen Sie eine bereits geleistete Anzahlung, die außerhalb des CRM ausgestellt wurde.
              Wird vom Auftragsendbetrag abgezogen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="adv-invoice-number">Rechnungsnummer *</Label>
              <Input
                id="adv-invoice-number"
                value={advanceForm.invoice_number}
                onChange={(e) => setAdvanceForm({ ...advanceForm, invoice_number: e.target.value })}
                placeholder="z. B. RE-2026-1234"
                data-testid="ext-advance-invoice-number"
              />
            </div>
            <div>
              <Label htmlFor="adv-amount">Betrag (€, brutto) *</Label>
              <Input
                id="adv-amount"
                type="number"
                step="0.01"
                min="0"
                value={advanceForm.amount}
                onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                placeholder="z. B. 5000.00"
                data-testid="ext-advance-amount"
              />
            </div>
            <div>
              <Label htmlFor="adv-date">Zahlungsdatum</Label>
              <Input
                id="adv-date"
                type="date"
                value={advanceForm.payment_date}
                onChange={(e) => setAdvanceForm({ ...advanceForm, payment_date: e.target.value })}
                data-testid="ext-advance-date"
              />
            </div>
            <div>
              <Label htmlFor="adv-notes">Bemerkungen</Label>
              <Input
                id="adv-notes"
                value={advanceForm.notes}
                onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })}
                placeholder="z. B. Anzahlung 30 % vor Lieferung"
                data-testid="ext-advance-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdvanceDialog(false); setReopenFinalAfterAdvance(false); }} disabled={savingAdvance}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveAdvance} disabled={savingAdvance} data-testid="ext-advance-save-button">
              {savingAdvance ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editingAdvanceId ? "Aktualisieren" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
